import type { components, operations } from '@multaqa/api-client'
import { type UseQueryOptions, useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { api, apiOrigin } from '@/lib/api'
import { downloadFile } from '@/lib/download'

// Display order, not the backend's order. The three states that mean "no
// attendance was expected today" sit together at the end.
export const BOARD_STATES = [
  'present',
  'late',
  'not_due',
  'absent',
  'excused_absence',
  'on_leave',
  'no_fixed_hours',
  'off',
] as const
export type AttendanceBoardState = (typeof BOARD_STATES)[number]
export type AttendanceBoardSort = 'name' | 'department' | 'state' | 'lateness'
export type AttendanceBoardDirection = 'asc' | 'desc'

export interface AttendanceBoardSearch {
  date?: string
  department_id?: string
  schedule_id?: string
  search?: string
  state?: AttendanceBoardState
  sort: AttendanceBoardSort
  direction: AttendanceBoardDirection
  page: number
  per_page: number
}

const localizedValue = z
  .object({ en: z.string().nullable().optional(), ar: z.string().nullable().optional() })
  .passthrough()
const apiNumber = z.coerce.number().finite()

const boardRow = z.object({
  employee_id: z.string(),
  employee_name: localizedValue,
  department: z.object({ id: z.string(), name: localizedValue }).nullable(),
  schedule: z.object({ id: z.string(), name: localizedValue }).nullable(),
  scheduled_start: z.string().nullable(),
  scheduled_end: z.string().nullable(),
  checked_in_at: z.string().nullable(),
  checked_out_at: z.string().nullable(),
  worked_minutes: apiNumber.nullable(),
  scheduled_minutes: apiNumber.nullable(),
  work_balance_minutes: apiNumber.nullable(),
  state: z.enum(BOARD_STATES),
  late_minutes: apiNumber.nullable(),
  is_open: z.boolean(),
  session_count: apiNumber.optional(),
  // f416: an open break session, and the day's closed break minutes (0 on a
  // one-session day whose schedule break was deducted instead).
  on_break: z.boolean().optional(),
  break_minutes: apiNumber.optional(),
  // The pending unverified kiosk punch waiting on this day, if any (ticket 06).
  // Optional so an older fixture that predates the badge still parses.
  pending_kiosk_punch_id: z.string().nullable().optional(),
  // The approved permission that absorbed the lateness. An excused arrival is
  // `present` with zero late minutes, so this is the only thing on the row that
  // says the day was ever late.
  excuse: z
    .object({
      approver_name: z.string().nullable(),
      reason: z.string(),
      minutes: apiNumber.nullable(),
      kind: z.string(),
      approved_at: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
})

const dailyAttendance = z.object({
  date: z.string(),
  rows: z.array(boardRow),
  totals: z.object({
    present: apiNumber,
    late: apiNumber,
    not_due: apiNumber,
    absent: apiNumber,
    excused_absence: apiNumber,
    on_leave: apiNumber,
    no_fixed_hours: apiNumber,
    off: apiNumber,
    headcount: apiNumber,
  }),
  carry_over: z.object({
    date: z.string(),
    count: apiNumber,
    rows: z.array(boardRow),
  }),
  meta: z.object({
    calculated_at: z.string(),
    timezone: z.string(),
    is_today: z.boolean(),
    total: apiNumber,
    page: apiNumber,
    per_page: apiNumber,
    last_page: apiNumber,
  }),
})

export type AttendanceBoardRow = z.infer<typeof boardRow>
export type DailyAttendance = z.infer<typeof dailyAttendance>
export type WorkSchedule = components['schemas']['WorkScheduleResource']
export type AttendanceExcuseRequest = components['schemas']['AttendanceExcuseRequest']
export type AttendanceAbsenceExcuseRequest = components['schemas']['AttendanceAbsenceExcuseRequest']
export type AttendanceExcuse = components['schemas']['PermissionRequestResource']

export function boardText(value: AttendanceBoardRow['employee_name'], locale: string): string {
  const preferred = locale.split('-')[0] === 'ar' ? value.ar : value.en
  const candidates = [preferred, value.en, value.ar]
  return candidates.find((candidate) => typeof candidate === 'string' && candidate.length > 0) ?? ''
}

export async function excuseAttendance(
  companyId: string,
  body: AttendanceExcuseRequest,
): Promise<AttendanceExcuse> {
  const response = await api.POST('/hr/attendance/excuse', {
    headers: { 'X-Company-Id': companyId },
    body,
  })

  if (response.error) throw response.error
  if (!response.data) throw new Error('POST /hr/attendance/excuse returned no body')

  return response.data.data
}

export async function excuseAbsence(
  companyId: string,
  body: AttendanceAbsenceExcuseRequest,
): Promise<AttendanceExcuse> {
  const response = await api.POST('/hr/attendance/excuse-absence', {
    headers: { 'X-Company-Id': companyId },
    body,
  })

  if (response.error) throw response.error
  if (!response.data) throw new Error('POST /hr/attendance/excuse-absence returned no body')

  return response.data.data
}

const attendanceBoardKey = (companyId: string | null) =>
  ['companies', companyId, 'hr', 'attendance-board'] as const

export const dailyAttendanceKeys = {
  all: attendanceBoardKey,
  lists: (companyId: string | null) => [...attendanceBoardKey(companyId), 'daily'] as const,
  list: (companyId: string | null, search: AttendanceBoardSearch) =>
    [...dailyAttendanceKeys.lists(companyId), search] as const,
  schedules: (companyId: string | null) => [...attendanceBoardKey(companyId), 'schedules'] as const,
}

type DailyAttendanceQuery = NonNullable<operations['hr.attendance.daily']['parameters']['query']>

function queryFor(search: AttendanceBoardSearch): DailyAttendanceQuery {
  const query: DailyAttendanceQuery = {
    department_id: search.department_id,
    schedule_id: search.schedule_id,
    search: search.search,
    state: search.state,
    sort: search.sort,
    direction: search.direction,
    page: search.page,
    per_page: search.per_page,
  }

  if (search.date) query.date = search.date

  return query
}

function parseDailyAttendance(value: unknown): DailyAttendance {
  return dailyAttendance.parse(value)
}

export function useDailyAttendance(
  companyId: string | null,
  search: AttendanceBoardSearch,
  options?: Omit<UseQueryOptions<DailyAttendance, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<DailyAttendance, Error>({
    queryKey: dailyAttendanceKeys.list(companyId, search),
    enabled: Boolean(companyId),
    staleTime: (query) => (query.state.data?.meta.is_today ? 30_000 : Number.POSITIVE_INFINITY),
    refetchInterval: (query) => (query.state.data?.meta.is_today ? 60_000 : false),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: (query) => query.state.data?.meta.is_today ?? false,
    ...options,
    queryFn: async () => {
      if (!companyId) throw new Error('An active company is required')

      const response = await api.GET('/hr/attendance/daily', {
        headers: { 'X-Company-Id': companyId },
        params: { query: queryFor(search) },
      })

      if (response.error) throw response.error
      if (!response.data) throw new Error('GET /hr/attendance/daily returned no body')

      // The generated operation supplies the outer JsonResource envelope. The
      // schema is parsed here so the feature only receives a trusted shape.
      return parseDailyAttendance(response.data.data)
    },
  })
}

export function useAttendanceBoardSchedules(companyId: string | null) {
  return useQuery<WorkSchedule[], Error>({
    queryKey: dailyAttendanceKeys.schedules(companyId),
    enabled: Boolean(companyId),
    staleTime: 5 * 60_000,
    queryFn: async () => {
      if (!companyId) return []

      const response = await api.GET('/hr/work-schedules', {
        headers: { 'X-Company-Id': companyId },
      })

      if (response.error) throw response.error
      if (!response.data) throw new Error('GET /hr/work-schedules returned no body')

      return response.data.data
    },
  })
}

export type AttendanceBoardExportFormat = 'csv' | 'pdf'

export function dailyAttendanceExportUrl(
  format: AttendanceBoardExportFormat,
  search: AttendanceBoardSearch,
): string {
  const params = new URLSearchParams({
    format,
    sort: search.sort,
    direction: search.direction,
  })

  if (search.date) params.set('date', search.date)

  if (search.department_id) params.set('department_id', search.department_id)
  if (search.schedule_id) params.set('schedule_id', search.schedule_id)
  if (search.search) params.set('search', search.search)
  if (search.state) params.set('state', search.state)

  return `${apiOrigin}/api/hr/attendance/daily/export?${params.toString()}`
}

export function exportDailyAttendance(
  companyId: string,
  format: AttendanceBoardExportFormat,
  search: AttendanceBoardSearch,
): Promise<void> {
  return downloadFile(
    dailyAttendanceExportUrl(format, search),
    `attendance-daily${search.date ? `-${search.date}` : ''}.${format}`,
    { 'X-Company-Id': companyId },
  )
}
